/**
 * HtmlCssSection — renders admin-authored HTML/CSS/JS in a sandboxed iframe.
 * JS executes inside the iframe but cannot access the parent frame.
 * Theme CSS variables are injected into the iframe :root so custom CSS
 * can use var(--primary-color), var(--secondary-color), etc.
 *
 * Bootstrap + Bootstrap Icons are injected by the platform, at the same
 * versions the admin's HtmlCssWorkspaceModal preview uses — keep the two in
 * step or the workspace preview stops matching what the client renders.
 * Admin-authored HTML must therefore NOT carry its own Bootstrap CDN tags.
 * Bootstrap's JS is a classic external <script> placed before the section's
 * own JS so it is fully evaluated first: `data-bs-ride="carousel"` markup and
 * section JS calling `bootstrap.Collapse` both depend on that ordering.
 *
 * Two render modes (section_config.render_mode):
 *   'inline' (default) — iframe is sized to its content via a postMessage
 *       height report, so the section flows with the rest of the page.
 *   'fullpage' — iframe fills the viewport and scrolls internally. Required
 *       for pasted full websites: `100vh`, `position: fixed` navbars,
 *       scroll-spy and `#anchor` smooth-scroll only behave correctly when the
 *       iframe itself is the scroll container. In inline mode the iframe has
 *       no scrollport of its own (it is sized to content), so `100vh` resolves
 *       against the reported height and in-page anchors cannot scroll.
 *
 * section_config.link_navigations (if present) maps links/buttons in the
 * admin's markup to Page Builder pages. The sandbox blocks the iframe from
 * navigating the app itself, so the injected bridge posts a nav id up and the
 * navigation happens here, in the parent React scope, via react-router. The
 * message only ever carries an id — the destination is resolved from this
 * component's own config, so section JS cannot navigate anywhere unconfigured.
 *
 * data_binding.bindings (if present) are resolved to public table record
 * lists here, in the parent React scope — this is where the app's public
 * table auth/key-store already lives, and the sandboxed iframe (no
 * allow-same-origin) has no network access of its own. Resolved records are
 * injected as window.ARCEE_DATA before the admin's own <script> tag runs,
 * with window.ARCEE_ERRORS alongside it naming any binding that failed.
 *
 * Each instance gets a unique ID embedded in the srcdoc so that postMessage
 * height reports are scoped — multiple sections on the same page don't
 * overwrite each other's heights.
 *
 * Scrollbars are hidden via CSS (scrollbar-width / ::-webkit-scrollbar) rather
 * than overflow:hidden so scrollHeight still reports the true content height.
 */

import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useQueries } from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { publicTableListQueryOptions } from '../../../hooks/usePublicTableView';
import {
  buildListParams,
  buildArceeData,
  serializeArceeData,
  buildCardConfig,
  buildCardScript,
  buildArceePagination,
  buildQueryParams,
  buildQueryScript,
  describeBindingError,
  QUERY_MESSAGE_TYPE,
  QUERY_RESULT_TYPE,
} from '../../../utils/htmlCssDataBindings';
import {
  NAV_MESSAGE_TYPE,
  buildLinkNavScript,
  resolveNavHref,
  isInternalHref,
  isRecordHref,
  fillRecordHref,
} from '../../../utils/htmlLinkNav';

let _counter = 0;

// Keep in step with arcee-admin HtmlCssWorkspaceModal's preview iframe.
const BOOTSTRAP_CSS   = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css';
const BOOTSTRAP_ICONS = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css';
const BOOTSTRAP_JS    = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js';

// A later height report may exceed the applied height by at most this factor
// before it is treated as vh feedback rather than genuine content growth.
const GROWTH_LIMIT = 1.2;

export default function HtmlCssSection({ config, dataBinding, theme, appBranding, embeddedFooterHtml = '' }) {
  const iframeRef  = useRef(null);
  const instanceId = useRef('arcee_iframe_' + (++_counter));
  const appliedHeight = useRef(0);
  const navigate = useNavigate();

  // Read through a ref so the message listener is subscribed once rather than
  // on every render — link_navigations is a fresh array identity each time.
  const linkNavigations = config?.link_navigations || [];
  const navsRef = useRef(linkNavigations);
  navsRef.current = linkNavigations;

  const bindings = dataBinding?.bindings || [];
  const bindingsWithTable = bindings.filter((b) => b.table_slug);

  const queries = useQueries({
    queries: bindingsWithTable.map((b) => publicTableListQueryOptions(b.table_slug, buildListParams(b))),
  });

  const isLoading = queries.some((q) => q.isLoading);

  // Section queries (arceeQuery) are answered from the stored bindings, read
  // through a ref so the listener is subscribed once.
  const queryClient = useQueryClient();
  const bindingsRef = useRef(bindingsWithTable);
  bindingsRef.current = bindingsWithTable;

  const isFullPage = config?.render_mode === 'fullpage';

  useEffect(() => {
    // Full-page sections have a fixed viewport height — nothing to resize.
    if (isFullPage) return undefined;
    const myId = instanceId.current;
    function onMessage(e) {
      if (e.data?.type === 'ARCEE_IFRAME_H' && e.data.id === myId && iframeRef.current) {
        const h = e.data.h;
        const applied = appliedHeight.current;
        // Guard against the vh feedback loop: content sized in viewport units
        // (a 100vh hero) grows to whatever height we just applied, which grows
        // the reported height, and so on without bound. Accept the first
        // measurement, then only refinements — a report that balloons past the
        // applied height is the loop, not real content. Sections that genuinely
        // need viewport units belong in 'fullpage' mode, where vh is stable.
        if (applied > 0 && h > applied * GROWTH_LIMIT) return;
        appliedHeight.current = h;
        iframeRef.current.style.height = h + 'px';
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isFullPage]);

  // Configured link/button navigation. The iframe is sandboxed without
  // allow-same-origin, so its postMessage origin is "null" and cannot be
  // checked — the instance id scopes the message to this section, and the
  // destination is looked up from config rather than taken from the message.
  useEffect(() => {
    const myId = instanceId.current;
    function onNavMessage(e) {
      if (e.data?.type !== NAV_MESSAGE_TYPE || e.data.id !== myId) return;
      const nav = navsRef.current.find((n) => n.id === e.data.navId);
      const configured = resolveNavHref(nav);
      if (!configured) return;
      // A destination written as `/p/event/{id}` opens whichever card was
      // clicked, so one configured link serves every row rendered from bound
      // data. Only the id travels up; the path itself is this component's own
      // config, so section markup cannot navigate anywhere unconfigured.
      const href = isRecordHref(configured)
        ? fillRecordHref(configured, e.data.recordId)
        : configured;
      if (!href) return;
      if (nav.openInNewTab) {
        window.open(href, '_blank', 'noopener');
      } else if (isInternalHref(href)) {
        navigate(href);
        window.scrollTo(0, 0);
      } else {
        window.location.href = href;
      }
    }
    window.addEventListener('message', onNavMessage);
    return () => window.removeEventListener('message', onNavMessage);
  }, [navigate]);

  // arceeQuery: a section asking for another page / a search of one of ITS
  // OWN bindings. Accepted only from this instance's own frame (the id AND the
  // window must match), and only for a div_id this section binds; the table
  // comes from the stored binding, never from the message. The answer goes
  // back to that frame only. See buildQueryParams for what may be asked.
  useEffect(() => {
    const myId = instanceId.current;
    function onQueryMessage(e) {
      if (e.data?.type !== QUERY_MESSAGE_TYPE || e.data.id !== myId) return;
      const frame = iframeRef.current?.contentWindow;
      if (!frame || e.source !== frame) return;

      const { reqId, divId, params } = e.data;
      const reply = (payload) => frame.postMessage({ type: QUERY_RESULT_TYPE, reqId, ...payload }, '*');
      const binding = bindingsRef.current.find((b) => b.div_id === divId);
      if (!binding) {
        reply({ ok: false, error: describeBindingError({ kind: 'not_found' }) });
        return;
      }

      queryClient
        .fetchQuery(publicTableListQueryOptions(binding.table_slug, buildQueryParams(binding, params)))
        .then((data) => {
          // The section is about to redraw with a different number of rows —
          // genuine content change, not vh feedback — so its next height
          // report is taken as a fresh first measurement.
          appliedHeight.current = 0;
          reply({
            ok: true,
            records: data?.records || data?.results || [],
            pagination: data?.pagination || null,
            related: data?.related || {},
          });
        })
        .catch((error) => reply({ ok: false, error: describeBindingError(error) }));
    }
    window.addEventListener('message', onQueryMessage);
    return () => window.removeEventListener('message', onQueryMessage);
  }, [queryClient]);

  // Bindings settle before the iframe is (re)built at all, avoiding a
  // flicker/rebuild-after-mount once data arrives.
  if (isLoading) {
    return <div style={{ width: '100%', minHeight: 100 }} />;
  }

  const html = config?.html || '';
  const css  = config?.css  || '';
  const js   = config?.js   || '';

  const primary   = theme?.primary   || '#4e2e80';
  const secondary = theme?.secondary || '#ff7a59';
  const gradient  = theme?.gradientBackground || `linear-gradient(135deg, ${primary}, #1a1a2e)`;

  const id = instanceId.current;
  // `errors` is reported beside the data rather than inside it: ARCEE_DATA
  // stays exactly what every existing section expects (an array per div_id,
  // empty when the fetch failed), and ARCEE_ERRORS carries WHY it is empty for
  // the sections that need to say something true about it.
  const { data: arceeData, errors: arceeErrors } = buildArceeData(bindings, queries);
  const arceeDataJson = serializeArceeData(arceeData);
  const arceeErrorsJson = serializeArceeData(arceeErrors);
  // Page counts for the first fetch, so a paging section knows its size
  // before it asks for anything.
  const arceePaginationJson = serializeArceeData(buildArceePagination(bindings, queries));
  // Only a section with bindings has anything to query.
  const queryScript = bindingsWithTable.length ? buildQueryScript(id) : '';

  // Card button/badge configuration, keyed by the same div_id as the data.
  // `cards` is the current key; `card_buttons` is read as a fallback so
  // sections saved before the rename keep rendering without a migration.
  // The two resolvers are stringified INTO the sandbox rather than duplicated
  // there: one definition, unit-tested here, running in both places. They are
  // written to close over nothing precisely so this survives minification.
  const arceeCardsJson = serializeArceeData(buildCardConfig(config?.cards || config?.card_buttons));
  const cardScript = buildCardScript();

  // Inline mode hides the scrollbar via scrollbar-width rather than
  // overflow:hidden, so scrollHeight still reports the true content height.
  // Full-page mode is the scroll container itself, so its scrollbar stays.
  const modeCss = isFullPage
    ? `html, body { height: 100%; }`
    : `body { scrollbar-width: none; }
body::-webkit-scrollbar { display: none; }`;

  // Full-page mode: the iframe is the scroll container, so in-page anchors are
  // handled here directly. A bare `href="#id"` would be a fragment navigation
  // against the frame's `about:srcdoc` base URL, which an opaque-origin sandbox
  // (no allow-same-origin) blocks — hence the console error. Intercepting the
  // click and scrolling the element into view sidesteps navigation entirely and
  // gives smooth scrolling regardless of the author's CSS.
  const modeScript = isFullPage
    ? `;(function(){
  document.addEventListener('click', function(e){
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var href = a.getAttribute('href');
    if (!href || href === '#') return;
    var target = document.getElementById(href.slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();`
    : `;(function(){
  var IFRAME_ID = '${id}';
  function report() {
    var h = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    parent.postMessage({ type: 'ARCEE_IFRAME_H', id: IFRAME_ID, h: h }, '*');
  }
  // Re-run by arceeQuery after a section redraws with new rows.
  window.__arceeReportHeight = report;
  if (document.readyState === 'complete') { report(); } else { window.addEventListener('load', report); }
  setTimeout(report, 300);
})();`;

  // Admin-configured branding, so pasted markup never hardcodes an asset path.
  // Any element carrying data-arcee-branding="<key>" is filled from this object:
  // <img> gets its src, anything else gets the value as text. Elements whose key
  // is unset are hidden rather than left as a broken image.
  const brandingJson = JSON.stringify({
    logo:         appBranding?.logo || '',
    banner_image: appBranding?.banner_image || '',
    app_name:     appBranding?.app_name || '',
  });

  // Emitted after the author's own JS so elements it renders at parse time are
  // already bindable; the script re-binds on load for anything created later.
  const linkNavScript = buildLinkNavScript(linkNavigations, id);

  const brandingScript = `;(function(){
  var B = window.ARCEE_BRANDING || {};
  var nodes = document.querySelectorAll('[data-arcee-branding]');
  for (var i = 0; i < nodes.length; i++) {
    var el = nodes[i];
    var val = B[el.getAttribute('data-arcee-branding')];
    if (!val) { el.style.display = 'none'; continue; }
    if (el.tagName === 'IMG') { el.src = val; } else { el.textContent = val; }
  }
})();`;

  // The section's own CSS is emitted at the END of <body>, after the pasted
  // markup. Authors routinely paste a whole document whose <head> carries its
  // own Bootstrap <link>; the parser relocates those into the body, where they
  // would otherwise land AFTER a <head> <style> and override the author's own
  // rules at equal specificity (a .btn-uv-accent background losing to .btn).
  // Emitting author CSS last keeps it winning regardless of what the paste
  // drags in. Theme variables ride along — :root applies wherever it is defined.
  const authorStyle = `<style>
:root {
  --primary-color: ${primary};
  --secondary-color: ${secondary};
  --gradient-bg: ${gradient};
}
html, body { margin: 0; font-family: sans-serif; }
${modeCss}
${css}
</style>`;

  // In full-page mode this iframe IS the page's scroll container, so the footer
  // is injected here rather than rendered after the iframe — otherwise the
  // visitor gets a second scrollbar just for the footer. Emitted after the
  // author's CSS so the author cannot accidentally restyle it, and only in
  // full-page mode (inline iframes flow with the page, so the parent renders
  // the real React footer below them as usual).
  const footerBlock = isFullPage && embeddedFooterHtml ? embeddedFooterHtml : '';

  const srcdoc = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link rel="stylesheet" href="${BOOTSTRAP_CSS}" />
<link rel="stylesheet" href="${BOOTSTRAP_ICONS}" />
</head>
<body>
${html}
${authorStyle}
${footerBlock}
<script src="${BOOTSTRAP_JS}"></script>
<script>window.ARCEE_DATA = ${arceeDataJson};
window.ARCEE_ERRORS = ${arceeErrorsJson};
window.ARCEE_PAGINATION = ${arceePaginationJson};
window.ARCEE_CARDS = ${arceeCardsJson};
window.ARCEE_BRANDING = ${brandingJson};</script>
<script>
${brandingScript}
${cardScript}
${queryScript}
${js}
${linkNavScript}
${modeScript}
<\/script>
</body>
</html>`;

  // allow-popups lets target="_blank" links (app store badges, socials) open.
  //
  // allow-same-origin is present for one concrete reason: WITHOUT it the frame
  // gets an opaque origin, Chrome site-isolates it into its own process, and
  // the browser's DEVICE-MODE INPUT PATH CANNOT HIT-TEST INTO IT. Measured on a
  // section with two buttons: a click aimed at a button was delivered to the
  // TOP document (`mousedown, click`) while the frame's own document received
  // nothing at all. Every control inside any such section is dead in the device
  // toolbar — no template or CSS change can reach it, because the event never
  // arrives. Real devices are unaffected; this is a preview-mode limitation.
  //
  // THE TRADE-OFF IS REAL AND DELIBERATE. `allow-same-origin` together with
  // `allow-scripts` lets the framed document reach the parent — including
  // removing its own sandbox. That is acceptable HERE and only here because
  // this markup is authored in the admin by someone who already controls the
  // application; it is not visitor-supplied content. If these sections ever
  // accept untrusted authoring, this must be reverted and device-mode
  // previewing given up instead.
  const sandbox = isFullPage
    ? 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-same-origin'
    : 'allow-scripts allow-forms allow-same-origin';

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      sandbox={sandbox}
      style={
        isFullPage
          ? { width: '100%', height: '100vh', border: 'none', display: 'block' }
          : { width: '100%', border: 'none', display: 'block', minHeight: 100 }
      }
      title="Custom HTML/CSS/JS Section"
    />
  );
}
